import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '@/config/reactQuery';
import { QUERY_KEYS } from '../../queryKeys';
import { questionnaireTemplates } from '../QuestionnaireTemplates';
import type { TemplateGrouping } from '../types';

const invalidateTemplateList = () =>
  queryClient.invalidateQueries({
    queryKey: [QUERY_KEYS.questionnaireTemplateList],
  });

export const useTemplateList = (
  customerIds: number[],
  grouping: TemplateGrouping,
) =>
  useQuery({
    queryKey: [QUERY_KEYS.questionnaireTemplateList, customerIds, grouping],
    queryFn: async () =>
      (await questionnaireTemplates.listTemplates({ customerIds, grouping }))
        .data,
    enabled: customerIds.length > 0,
  });

export const useTemplateForEdit = (id?: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.questionnaireTemplateEdit, id],
    queryFn: async () => {
      const res = await questionnaireTemplates.getTemplateForEdit(id!);
      if (!res.success) throw res;
      return res.data;
    },
    enabled: !!id,
  });

export const useCreateTemplate = () =>
  useMutation({
    mutationFn: questionnaireTemplates.createTemplate,
    onSuccess: (res) => {
      if (res.success) invalidateTemplateList();
    },
  });

export const useUpdateTemplate = () =>
  useMutation({
    mutationFn: questionnaireTemplates.updateTemplate,
    onSuccess: (res) => {
      if (res.success) invalidateTemplateList();
    },
  });

export const useDeactivateTemplate = () =>
  useMutation({
    mutationFn: questionnaireTemplates.deactivateTemplate,
    onSuccess: (res) => {
      if (res.success) invalidateTemplateList();
    },
  });

export const usePublishTemplate = () =>
  useMutation({
    mutationFn: questionnaireTemplates.publishTemplate,
    onSuccess: (res) => {
      if (res.success) invalidateTemplateList();
    },
  });
